import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import type { CharacterSummary } from '../api';
import { DeleteModal } from '../components/DeleteModal';
import { Gump } from '../components/Gump';
import { t } from '../i18n/idioma';
import { nomeDaNotoriedade, nomeDaProfissao } from '../i18n/nomes';
import { nickFillFromLabel } from '../look/nickColor';
import { useSession } from '../state/SessionContext';
import { ehShardUnico } from '../state/shardUnico';

const SKILL_CAP = 500;
const SLOTS = 5;

export function CharacterScreen() {
  const { shard, enterWorld, back, startCreate, signOut } = useSession();
  const [chars, setChars] = useState<readonly CharacterSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<CharacterSummary | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState<string | null>(null);

  const reload = () => {
    if (!shard) return;
    api.listCharacters(shard.id)
      .then(setChars)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : t('personagem.naoListou'));
      });
  };

  useEffect(() => {
    if (!shard) return;
    let live = true;
    api.listCharacters(shard.id)
      .then(list => { if (live) setChars(list); })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : t('personagem.naoListou'));
      });
    return () => { live = false; };
  }, [shard]);

  const used = chars?.length ?? 0;
  const canCreate = used < SLOTS && shard?.status === 'aberto';

  return (
    <main className="screen">
      <div className="screen__inner screen__inner--wide">
        <div className="brand">
          <h1>{shard?.name ?? t('personagem.personagens')}</h1>
          <p>{t('personagem.mantemAte', { total: SLOTS })}</p>
        </div>

        <Gump title={t('personagem.titulo', { usados: used, total: SLOTS })}>
          {error && <p className="notice" role="alert">{error}</p>}

          {!chars && !error && <p className="empty">{t('personagem.abrindo')}</p>}

          {chars?.length === 0 && (
            <p className="empty">{t('personagem.nenhumNesteShard')}</p>
          )}

          {chars && chars.length > 0 && (
            <div className="picklist">
              {chars.map(character => (
                <div key={character.id} className="pickwrap">
                  <button
                    type="button"
                    className="pick"
                    onClick={() => enterWorld(character)}
                  >
                    <span className="pick__name">{character.name}</span>
                    <span
                      className="pick__tag"
                      style={{
                        color: nickFillFromLabel(character.notoriety),
                        borderColor: nickFillFromLabel(character.notoriety)
                      }}
                    >
                      {nomeDaNotoriedade(character.notoriety)}
                    </span>
                    <span className="pick__meta">
                      {nomeDaProfissao(character.profession, character.profession)} · {character.homeCity} ·{' '}
                      {t('personagem.emSkills', { atual: character.skillTotal, total: SKILL_CAP })} ·{' '}
                      {t('personagem.visto', { quando: character.lastSeen })}
                    </span>
                    <span className="bar-mini">
                      <i style={{ width: `${(character.skillTotal / SKILL_CAP) * 100}%` }} />
                    </span>
                  </button>
                  {/* Dentro do cartão do personagem (fica fora do <button> do cartão: botão não entra em botão). */}
                  <div className="pick__ops">
                    <button type="button" className="btn btn--tiny btn--danger" onClick={() => { setDelError(null); setDeleting(character); }}>
                      {t('comum.excluir')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Gump>

        <div className="row row--split">
          <div className="row">
            {/* Com um shard só, não há para onde trocar (Renato, 04/11). */}
            {!ehShardUnico() && (
              <button type="button" className="btn btn--ghost" onClick={back}>
                {t('shard.trocarDeShard')}
              </button>
            )}
            {/* Para trocar de conta sem voltar ao shard (Renato, 25/10). Vermelho: sai da conta (Renato, 04/11). */}
            <button type="button" className="btn btn--danger" onClick={() => void signOut()}>
              {t('auth.sairDaConta')}
            </button>
          </div>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canCreate}
            onClick={startCreate}
          >
            {t('personagem.criar')}
          </button>
        </div>
      </div>

      {deleting && shard && (
        <DeleteModal
          name={deleting.name}
          busy={delBusy}
          error={delError}
          onCancel={() => setDeleting(null)}
          onConfirm={async typed => {
            setDelBusy(true);
            setDelError(null);
            try {
              await api.deleteCharacter(shard.id, deleting.id, typed);
              setDeleting(null);
              reload();
            } catch (err) {
              setDelError(err instanceof ApiError ? err.message : t('personagem.naoExcluiu'));
            } finally {
              setDelBusy(false);
            }
          }}
        />
      )}

    </main>
  );
}
